import React, { type FC, useCallback, useMemo } from "react";
import { useActionSheet, useIsOverlayOpenByKeys, useWindows } from "@app/overlay";
import { useBoAvOperations, type ApiOmpBoAvOperation } from "@src/api/bo";
import { CardHeader } from "@shared/ui/card-header";
import { CardLoader } from "@src/components/card-loader";
import {
    BusinessBoStateValue,
    BusinessDateValue,
    BusinessDivisionValue,
    formatBoStateText,
    formatDivisionText,
} from "@src/components/business-data-display";
import { useBoOperationActionItems } from "@src/businessModules/WorkShop/shared/bo/useBoOperationActionItems";
import { useBoOperationConfirm } from "@src/businessModules/WorkShop/shared/bo/useBoOperationConfirm";
import { useRequiredNumberParam } from "@src/hooks/useRequiredNumberParam";
import { DetailList } from "@shared/ui/detail-row";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import type { ActOfCheckingAvailability } from "../../../../api/actOfCheckingAvailability";
import { StockPartialInventoryContentsList } from "./components/StockPartialInventoryContentsList/StockPartialInventoryContentsList";
import { StockPartialInventoryScanPlaceholder } from "./components/StockPartialInventoryScanPlaceholder/StockPartialInventoryScanPlaceholder";
import { isStockPartialInventoryContentLotBased } from "./helpers/formatStockPartialInventoryContent";
import { useStockPartialInventoryCardQuery, useStockPartialInventoryContentsQuery } from "./hooks";
import { stockPartialInventoryCardWindows } from "./stockPartialInventoryCard.windows";
import { useStockPartialInventoryCardMutations } from "./useStockPartialInventoryCardMutations";
import "./StockPartialInventoryCard.scss";

const STOCK_PARTIAL_INVENTORY_CARD_SCAN_PAUSE_WINDOW_KEYS = [stockPartialInventoryCardWindows.content.key];

/** Карточка частичной инвентаризации склада по АПН. */
export const StockPartialInventoryCard: FC = () => {
    const actCode = useRequiredNumberParam("code", "код АПН");
    const windows = useWindows();
    const actionSheet = useActionSheet();

    const cardQuery = useStockPartialInventoryCardQuery(actCode);
    const contentsQuery = useStockPartialInventoryContentsQuery(actCode);
    const act = cardQuery.data;
    const avOperationsQuery = useBoAvOperations(act?.Code, act != null);
    const isCardScannerSuppressedByWindow = useIsOverlayOpenByKeys(STOCK_PARTIAL_INVENTORY_CARD_SCAN_PAUSE_WINDOW_KEYS);

    const mutations = useStockPartialInventoryCardMutations({
        onContentRefetch: (contentCode) => contentsQuery.refetchContentByCode(contentCode),
        onContentUpsert: (content) => contentsQuery.upsertContent(content),
        onBoOperationSuccess: () => {
            void cardQuery.refetch();
            void contentsQuery.refetch();
            void avOperationsQuery.refetch();
        },
    });

    const { confirmAndPerform } = useBoOperationConfirm({
        perform: mutations.performBoOperationMutation.mutateAsync,
    });

    const buildBoOperationParams = useCallback((operation: ApiOmpBoAvOperation, currentAct: ActOfCheckingAvailability) => {
        return {
            operation,
            boCode: currentAct.Code,
            documentLabel: `АПН №${currentAct.Num}`,
            fields: [
                { label: "Склад", value: formatEmptyValue(formatDivisionText(currentAct.Division)) },
                { label: "Статус", value: formatEmptyValue(formatBoStateText(currentAct.State)) },
            ],
        };
    }, []);

    const operationItems = useBoOperationActionItems({
        operations: avOperationsQuery.data,
        context: act,
        confirmAndPerform,
        buildParams: buildBoOperationParams,
    });

    const actionSheetSections = useMemo<ActionSheetSection[]>(() => {
        if (operationItems.length === 0) {
            return [];
        }

        return [
            {
                id: "bo",
                items: [
                    {
                        type: "drill-in",
                        id: "bo-operations",
                        label: "Выполнить операцию",
                        description: "Выбрать доступную БО-операцию",
                        sections: [{ id: "bo-operations", items: operationItems }],
                    },
                ],
            },
        ];
    }, [operationItems]);

    const openContentWindow = useCallback(
        (contentCode: number): void => {
            const contents = contentsQuery.getContentSnapshot();
            const content = contents.find((item) => item.Code === contentCode);
            if (content == null || !isStockPartialInventoryContentLotBased(content)) {
                return;
            }

            stockPartialInventoryCardWindows.content.open(windows, {
                contentCode,
                contents,
                updateFactQtiesMutation: mutations.updateFactQtiesMutation,
            });
        },
        [contentsQuery, mutations.updateFactQtiesMutation, windows],
    );

    if (cardQuery.isFetching || act == null) {
        return <CardLoader header list />;
    }

    return (
        <div className="stock_partial_inventory_card_page">
            <CardHeader
                text={`Частичная инвентаризация по АПН №${act.Num}`}
                buttons={[
                    {
                        type: "icon",
                        text: "Действия",
                        srcIcon: "/images/common/more_vert.svg",
                        onClick: () => actionSheet.open({ title: "Действия", sections: actionSheetSections }),
                    },
                ]}
            />
            <DetailList className="stock_partial_inventory_card_inform" layout="tiles" columns="auto" minWidth={96} oneLine>
                <DetailList.Row label="Статус АПН" value={<BusinessBoStateValue state={act.State} />} />
                <DetailList.Row label="Склад" value={<BusinessDivisionValue division={act.Division} />} />
                <DetailList.Row label="Дата" value={<BusinessDateValue value={act.Date} />} />
            </DetailList>
            <StockPartialInventoryContentsList contentsQuery={contentsQuery} onOpenContent={openContentWindow} />
            <StockPartialInventoryScanPlaceholder
                actCode={act.Code}
                divisionCode={act.DivisionCode}
                contents={contentsQuery.content}
                enabled={!contentsQuery.isFetching && !isCardScannerSuppressedByWindow}
                insertContentMutation={mutations.insertContentMutation}
                onOpenContent={openContentWindow}
            />
        </div>
    );
};

function formatEmptyValue(value: string): string {
    return value === "" ? "—" : value;
}
